import React, { useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Col, Row, Space } from 'antd';

import moment from 'moment';

import useForm, { FormContext } from '../../../hooks/useForm';

import FormWrapper from '../../FormWrapper';
import SaveButton from '../../buttons/SaveButton';
import CancelButton from '../../buttons/CancelButton';
import usePermissions from '../../../hooks/usePermission';

const TIME_FORMAT = 'HH:mm';
const WEEK_DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun', 'hol'];

const capitalize = (string) => string.charAt(0).toUpperCase() + string.slice(1);

const permissionsMap = {
  canEdit: ['SYS_SUPERUSER', 'OFFERS_PERMISSION'],
};

const EditScheduleForm = ({ schedule, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const { canEdit } = usePermissions(permissionsMap);
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        timeStart: yup.string().required(t('errors.notEmpty')),
      }),
    [t],
  );

  const form = useForm({
    initialValue: schedule,
    schema: validationSchema,
    onSubmit,
    onCancel,
  });

  const { handleSubmit, value, input, checkbox, custom, setFormValue } = form;

  useEffect(() => {
    setFormValue(schedule);
  }, [setFormValue, schedule]);

  const { deleted } = value;

  const timeCustom = useCallback(
    (name) => ({
      name,
      getValue: () => {
        const mins = value[name];
        return mins != null
          ? moment().startOf('day').add(mins, 'minutes')
          : null;
      },
      setValue: (_, time) =>
        custom(name).setValue(
          name,
          time ? moment.duration(time.format(TIME_FORMAT)).asMinutes() : null,
        ),
      invalid: custom(name).invalid,
    }),
    [custom, value],
  );

  return (
    <>
      <FormContext.Provider value={form}>
        <form onSubmit={handleSubmit}>
          <FormWrapper>
            <FormWrapper.Single>
              <FormWrapper.Input
                label={t('entity.offers.schedule.name')}
                props={{ ...input('name'), disabled: deleted || !canEdit }}
              />

              <FormWrapper.TimeRangePicker
                label={t('entity.offers.schedule.selectTime')}
                props={{
                  start: timeCustom('timeStart'),
                  end: timeCustom('timeEnd'),
                  format: TIME_FORMAT,
                  placeholder: [
                    t('entity.offers.schedule.timeStart'),
                    t('entity.offers.schedule.timeEnd'),
                  ],
                  allowEmpty: [false, false],
                  disabled: deleted || !canEdit,
                }}
              />

              <Row>
                {WEEK_DAYS.map((day) => (
                  <Col xs={24} sm={12} md={8} lg={6} xl={6} key={day}>
                    <FormWrapper.CheckBox
                      label={t(`entity.offers.schedule.days.${day}`)}
                      props={{
                        ...checkbox(`day${capitalize(day)}`),
                        disabled: deleted || !canEdit,
                      }}
                    />
                  </Col>
                ))}
              </Row>
            </FormWrapper.Single>
          </FormWrapper>

          <Space style={{ marginTop: '1rem' }}>
            {!deleted && canEdit && <SaveButton />}
            <CancelButton onCancel={onCancel} />
          </Space>
        </form>
      </FormContext.Provider>
    </>
  );
};

export default EditScheduleForm;
